<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import type { AuthUser } from '../../core/state/auth-store';
import type { HeroProfile } from '../game/hero-api';
import { CLASS_LABELS } from '../game/hero-api';
import {
  fetchAccount, changePassword, resendVerification, submitKyc, setCountry,
  updateProfile, deleteAccount, contactSupport, type AccountInfo,
} from '../game/account-api';
import {
  fetchTopupHistory, fetchInvoices, fetchLedger,
  type PurchaseEntry, type InvoiceEntry, type LedgerEntry,
} from '../game/topup-api';
import { downloadInvoice } from '../game/file-download';
import { fetchReferralInfo, type ReferralInfo } from '../game/referral-api';
import { fetchLegalPages, type LegalPageSummary } from '../game/legal-api';
import { classArt } from '../game/art';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import cashIcon from '../assets/omerta/icons/cash.webp';
import { toast } from '../game/toast';

const props = defineProps<{ user: AuthUser; hero: HeroProfile | null }>();
const emit = defineEmits<{ (e: 'goto-reserve'): void }>();

type Tab = 'account' | 'papers' | 'referral' | 'purchases' | 'invoices' | 'transactions';
const tab = ref<Tab>('account');

const account = ref<AccountInfo | null>(null);
const loading = ref(true);
const error = ref('');

const memberSince = computed(() => account.value
  ? new Date(account.value.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  : '');

async function loadAccount() {
  try {
    account.value = await fetchAccount();
    userCountry.value = account.value.country ?? '';
    syncProfileForm();
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
}
onMounted(loadAccount);

/**
 * Billing identity (Settings → Account → Profile).
 *
 * These four fields are what the invoice PDF prints under "Bill To", so the
 * form is deliberately plain: edit, Save, done. The form is re-hydrated from
 * the server response after every save, so what is on screen is what was stored.
 */
const pfFirstName = ref('');
const pfLastName = ref('');
const pfAddress = ref('');
const pfPhone = ref('');
const pfSaving = ref(false);

function syncProfileForm() {
  const a = account.value;
  if (!a) return;
  pfFirstName.value = a.firstName ?? '';
  pfLastName.value = a.lastName ?? '';
  pfAddress.value = a.address ?? '';
  pfPhone.value = a.phone ?? '';
}

const profileDirty = computed(() => {
  const a = account.value;
  if (!a) return false;
  return pfFirstName.value !== (a.firstName ?? '')
    || pfLastName.value !== (a.lastName ?? '')
    || pfAddress.value !== (a.address ?? '')
    || pfPhone.value !== (a.phone ?? '');
});

async function onSaveProfile() {
  if (!profileDirty.value) return;
  pfSaving.value = true;
  try {
    account.value = await updateProfile({
      firstName: pfFirstName.value,
      lastName: pfLastName.value,
      address: pfAddress.value,
      phone: pfPhone.value,
    });
    syncProfileForm();
    toast('Details saved — they will appear on your next invoice.');
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { pfSaving.value = false; }
}

/**
 * Contact support — emailed to the house address, with the player's own address
 * carried in the body so a human can reply straight to them.
 */
const supportSubject = ref('');
const supportMessage = ref('');
const supportSending = ref(false);
const supportValid = computed(() =>
  supportSubject.value.trim().length >= 3 && supportMessage.value.trim().length >= 10);

async function onContactSupport() {
  if (!supportValid.value) return;
  supportSending.value = true;
  try {
    await contactSupport(supportSubject.value.trim(), supportMessage.value.trim());
    supportSubject.value = '';
    supportMessage.value = '';
    toast('Message sent — the house will be in touch.');
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { supportSending.value = false; }
}

/**
 * Delete account. Two gates before anything happens — an explicit reveal of the
 * form, then the account password — because this ends the session for good and
 * there is no undo from the game side.
 */
const deleteOpen = ref(false);
const deletePassword = ref('');
const deleting = ref(false);

async function onDeleteAccount() {
  if (!deletePassword.value) return;
  deleting.value = true;
  try {
    await deleteAccount(deletePassword.value);
    // Every session was just revoked server-side; a full reload drops all
    // in-memory game state and lands on the login screen.
    location.href = '/login';
  } catch (e) {
    toast((e as Error).message, { kind: 'error' });
    deleting.value = false;
  }
}

// ── Billing country (dominion: Settings → Profile → Country, drives VAT) ──
const userCountry = ref('');
const countrySaving = ref(false);
async function onSaveCountry() {
  if (userCountry.value.length !== 2) return;
  countrySaving.value = true;
  try {
    await setCountry(userCountry.value);
    if (account.value) account.value.country = userCountry.value;
    toast('Country saved — future invoices will use it.');
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { countrySaving.value = false; }
}

// ── Email verification resend ──
const verifySending = ref(false);
const verifySent = ref(false);
async function onResend() {
  verifySending.value = true;
  try { await resendVerification(); verifySent.value = true; }
  catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { verifySending.value = false; }
}

// ── Change password ──
const pwCurrent = ref('');
const pwNew = ref('');
const pwConfirm = ref('');
const pwSaving = ref(false);
async function onSavePassword() {
  if (pwNew.value.length < 8) { toast('New password must be at least 8 characters.', { kind: 'error' }); return; }
  if (pwNew.value !== pwConfirm.value) { toast('Passwords do not match.', { kind: 'error' }); return; }
  pwSaving.value = true;
  try {
    await changePassword(pwCurrent.value, pwNew.value);
    pwCurrent.value = ''; pwNew.value = ''; pwConfirm.value = '';
    toast('Password changed.');
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { pwSaving.value = false; }
}

// ── KYC (Papers) ──
const kycName = ref('');
const kycDob = ref('');
const kycAddress = ref('');
const kycCountry = ref('');
const kycFile = ref<File | null>(null);
const kycSubmitting = ref(false);
const COUNTRIES: Array<[string, string]> = [
  ['AT', 'Austria'], ['BE', 'Belgium'], ['BG', 'Bulgaria'], ['HR', 'Croatia'],
  ['CY', 'Cyprus'], ['CZ', 'Czech Republic'], ['DK', 'Denmark'], ['EE', 'Estonia'],
  ['FI', 'Finland'], ['FR', 'France'], ['DE', 'Germany'], ['GR', 'Greece'],
  ['HU', 'Hungary'], ['IE', 'Ireland'], ['IT', 'Italy'], ['LV', 'Latvia'],
  ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MT', 'Malta'], ['NL', 'Netherlands'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['RO', 'Romania'], ['SK', 'Slovakia'],
  ['SI', 'Slovenia'], ['ES', 'Spain'], ['SE', 'Sweden'],
  ['GB', 'United Kingdom'], ['US', 'United States'],
];
function onKycFile(e: Event) {
  kycFile.value = (e.target as HTMLInputElement).files?.[0] ?? null;
}
const kycFormValid = computed(() =>
  kycName.value.trim().length > 1 && !!kycDob.value
  && kycAddress.value.trim().length > 3 && kycCountry.value.length === 2 && !!kycFile.value);
async function onSubmitKyc() {
  if (!kycFormValid.value || !kycFile.value) return;
  kycSubmitting.value = true;
  try {
    await submitKyc({
      fullName: kycName.value.trim(),
      dateOfBirth: kycDob.value,
      address: kycAddress.value.trim(),
      country: kycCountry.value,
      document: kycFile.value,
    });
    toast('Papers submitted — the Commission will review them.');
    await loadAccount();
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { kycSubmitting.value = false; }
}

/**
 * House papers — the published legal documents (Settings → Papers).
 *
 * The list comes from the admin CMS, same source the web footer reads, so a
 * document published in the admin panel appears here with no frontend change.
 * A failure only blanks this section: the KYC form above it must stay usable.
 */
const legalPages = ref<LegalPageSummary[]>([]);
const legalPagesLoaded = ref(false);
async function loadLegalPages() {
  if (legalPagesLoaded.value) return;
  try { legalPages.value = await fetchLegalPages(); }
  catch { /* leave the section empty — never break the Papers tab over it */ }
  finally { legalPagesLoaded.value = true; }
}

// ── Referral (Settings → Referral) ──
const referral = ref<ReferralInfo | null>(null);
const referralLoaded = ref(false);
// Local error, not the shared `error` ref above — a failed fetch here (e.g. the
// endpoint not deployed yet) should only blank this section, not the whole page.
const referralError = ref('');
const referralLink = computed(() => referral.value ? `${location.origin}/?ref=${referral.value.code}` : '');
const referralCopied = ref(false);
async function copyReferralLink() {
  if (!referralLink.value) return;
  try {
    await navigator.clipboard.writeText(referralLink.value);
    referralCopied.value = true;
    window.setTimeout(() => { referralCopied.value = false; }, 1800);
  } catch { /* clipboard permission denied — nothing to recover, just skip the flash */ }
}

// ── Billing records (moved out of the Gold Reserve — each its own page) ──
const purchases = ref<PurchaseEntry[]>([]);
const invoices = ref<InvoiceEntry[]>([]);
const ledger = ref<LedgerEntry[]>([]);
const purchasesLoaded = ref(false);
const invoicesLoaded = ref(false);
const ledgerLoaded = ref(false);
async function openTab(t: Tab) {
  tab.value = t;
  if (t === 'papers') { await loadLegalPages(); return; }
  if (t === 'referral' && !referralLoaded.value) {
    try { referral.value = await fetchReferralInfo(); }
    catch (e) { referralError.value = (e as Error).message || 'Could not load referral info.'; }
    finally { referralLoaded.value = true; }
    return;
  }
  try {
    if (t === 'purchases' && !purchasesLoaded.value) {
      purchases.value = await fetchTopupHistory();
      purchasesLoaded.value = true;
    } else if (t === 'invoices' && !invoicesLoaded.value) {
      invoices.value = await fetchInvoices();
      invoicesLoaded.value = true;
    } else if (t === 'transactions' && !ledgerLoaded.value) {
      ledger.value = await fetchLedger({ limit: 100 });
      ledgerLoaded.value = true;
    }
  } catch (e) { error.value = (e as Error).message; }
}

function price(cents: number): string { return `€${(cents / 100).toFixed(2)}`; }
function dateOf(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function timeOf(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Invoice PDFs are fetched, not linked. The API can sit on its own origin now
 * (`API_URL` is injected at runtime), so a root-relative href would hit the
 * static frontend, and the SameSite=Strict access cookie would not ride a
 * cross-site navigation. See `game/file-download.ts`.
 */
const downloading = ref<string | null>(null);
async function getPdf(invoiceNumber: string) {
  if (downloading.value) return;
  downloading.value = invoiceNumber;
  try {
    await downloadInvoice(invoiceNumber);
  } catch (e) {
    toast(`Could not download ${invoiceNumber} — ${(e as Error).message}`);
  } finally {
    downloading.value = null;
  }
}

const kycLabel = computed(() => {
  switch (account.value?.kycStatus) {
    case 'APPROVED': return 'Papers in order — identity verified';
    case 'PENDING': return 'Papers with the Commission — under review';
    case 'REJECTED': return 'Papers rejected';
    default: return 'Papers not submitted';
  }
});
</script>

<template>
  <section>
    <!-- header -->
    <div class="pb-4 border-b border-border/70 mb-5">
      <div class="font-label uppercase tracking-[0.3em] text-[9px] text-text-dim">Configuration</div>
      <h1 class="font-heading font-black text-[28px] text-[#f0e6d0] tracking-[0.05em] leading-tight">The Back Office</h1>
    </div>

    <p v-if="loading" class="text-text-dim">Loading&hellip;</p>
    <p v-else-if="error" class="text-sm text-error">{{ error }}</p>

    <div v-else class="st-body">
      <!-- sidebar nav (dominion Settings sidebar) -->
      <aside class="st-nav">
        <div class="st-nav-sec">Options</div>
        <button :class="['st-item', tab === 'account' && 'st-item-active']" @click="openTab('account')">Account</button>
        <button :class="['st-item', tab === 'papers' && 'st-item-active']" @click="openTab('papers')">Papers</button>
        <button :class="['st-item', tab === 'referral' && 'st-item-active']" @click="openTab('referral')">Referral</button>
        <div class="st-nav-sec mt-3">Billing</div>
        <button :class="['st-item', tab === 'purchases' && 'st-item-active']" @click="openTab('purchases')">Purchases</button>
        <button :class="['st-item', tab === 'invoices' && 'st-item-active']" @click="openTab('invoices')">Invoices</button>
        <button :class="['st-item', tab === 'transactions' && 'st-item-active']" @click="openTab('transactions')">Transactions</button>
        <template v-if="props.user.role === 'ADMIN'">
          <div class="st-nav-sec mt-3">Administration</div>
          <!-- /admin is Caddy's reverse proxy to the separate Kottster app;
               the in-game admin panel is the SPA route /admin-panel. -->
          <a class="st-item st-item-admin" href="/admin-panel" target="_blank" rel="noopener">Admin panel ↗</a>
        </template>
      </aside>

      <!-- content -->
      <div class="min-w-0 flex-1">

        <!-- ══ ACCOUNT ══ -->
        <template v-if="tab === 'account' && account">
          <div class="st-section">
            <div class="st-title">Account</div>

            <!-- email verification -->
            <div v-if="!account.emailVerified" class="st-plate st-plate--amber">
              <span>{{ verifySent ? 'Verification sent — check your inbox.' : 'Your email is not verified.' }}</span>
              <button v-if="!verifySent" class="btn-deco btn-deco--gold btn-deco--sm" :disabled="verifySending" @click="onResend">
                {{ verifySending ? '…' : 'Verify now' }}
              </button>
            </div>
            <div v-else class="st-plate st-plate--green">Email verified</div>

            <!-- KYC chip (only when the house checks papers) -->
            <div
              v-if="account.kycEnabled"
              class="st-plate"
              :class="{
                'st-plate--green': account.kycStatus === 'APPROVED',
                'st-plate--amber': account.kycStatus === 'PENDING',
                'st-plate--red': account.kycStatus === 'REJECTED',
                'st-plate--dim': account.kycStatus === 'NONE',
              }"
              :style="account.kycStatus !== 'APPROVED' ? 'cursor:pointer' : ''"
              @click="account.kycStatus !== 'APPROVED' && openTab('papers')"
            >
              <span>{{ kycLabel }}</span>
              <span v-if="account.kycStatus !== 'APPROVED'" class="ml-auto text-text-dim text-xs font-body italic">See Papers &rsaquo;</span>
            </div>

            <!-- account card -->
            <div class="st-card">
              <div class="st-avatar">
                <img v-if="props.hero" :src="classArt(props.hero.hero.class)" :alt="props.hero.hero.class" />
                <span v-else class="font-heading text-gold text-lg">{{ (account.username ?? '?').slice(0, 1).toUpperCase() }}</span>
              </div>
              <div class="min-w-0">
                <div class="font-heading font-black text-lg text-[#f0e6d0] tracking-wide truncate">
                  {{ (account.username ?? 'PLAYER').toUpperCase() }}
                </div>
                <div class="font-body text-sm text-text-dim truncate">{{ account.email }}</div>
                <div v-if="memberSince" class="font-body text-xs text-text-dim italic">Member since {{ memberSince }}</div>
                <div class="flex flex-wrap gap-2 mt-2">
                  <span v-if="props.hero" class="st-badge">
                    {{ CLASS_LABELS[props.hero.hero.class] }} · Lv.{{ props.hero.progression.level }}
                  </span>
                  <span v-if="props.hero" class="st-badge st-badge--gold">
                    <img :src="goldBarsIcon" class="w-3.5 h-3.5 object-contain" alt="" />
                    {{ props.hero.progression.goldBars.toLocaleString() }} bars
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="st-section">
            <div class="st-title">Profile</div>
            <div class="st-row">
              <div><div class="st-label">Username</div><div class="st-desc">Fixed at registration.</div></div>
              <input class="st-input" :value="account.username ?? ''" readonly />
            </div>
            <div class="st-row">
              <div><div class="st-label">Email</div><div class="st-desc">Where the house sends word.</div></div>
              <input class="st-input" :value="account.email" readonly style="min-width: 220px" />
            </div>
            <div class="st-row">
              <div><div class="st-label">Member since</div><div class="st-desc">The day you joined the family.</div></div>
              <input class="st-input" :value="memberSince" readonly />
            </div>
            <div class="st-row">
              <div><div class="st-label">Country</div><div class="st-desc">Billing country — used on your invoices.</div></div>
              <div class="flex items-center gap-2">
                <select v-model="userCountry" class="st-input" style="min-width: 170px">
                  <option value="" disabled>Select country…</option>
                  <option v-for="[code, name] in COUNTRIES" :key="code" :value="code">{{ name }}</option>
                </select>
                <button
                  class="btn-deco btn-deco--gold btn-deco--sm"
                  :disabled="countrySaving || userCountry.length !== 2 || userCountry === (account.country ?? '')"
                  @click="onSaveCountry"
                >{{ countrySaving ? '…' : 'Save' }}</button>
              </div>
            </div>
          </div>

          <!-- Billing identity. These are the details printed on invoice PDFs,
               which is why they are edited here rather than buried in KYC. -->
          <div class="st-section">
            <div class="st-title">Billing details</div>
            <p class="font-body text-sm text-text-dim mb-3">
              Printed on your invoices. Changes apply to invoices issued from now on —
              documents already issued keep the details they were issued with.
            </p>
            <div class="flex flex-col gap-3 max-w-md">
              <div><div class="st-label mb-1">Name</div>
                <input v-model="pfFirstName" class="st-input w-full" placeholder="First name" autocomplete="given-name" /></div>
              <div><div class="st-label mb-1">Surname</div>
                <input v-model="pfLastName" class="st-input w-full" placeholder="Last name" autocomplete="family-name" /></div>
              <div><div class="st-label mb-1">Phone <span class="text-text-dim italic">(optional)</span></div>
                <input v-model="pfPhone" class="st-input w-full" placeholder="+1 555 0100" autocomplete="tel" /></div>
              <div><div class="st-label mb-1">Address <span class="text-text-dim italic">(optional)</span></div>
                <input v-model="pfAddress" class="st-input w-full" placeholder="Street, city, postal code" autocomplete="street-address" /></div>
              <button
                class="btn-deco btn-deco--gold btn-deco--sm self-start"
                :disabled="pfSaving || !profileDirty"
                @click="onSaveProfile"
              >{{ pfSaving ? 'Saving…' : 'Save details' }}</button>
            </div>
          </div>

          <div class="st-section">
            <div class="st-title">Change password</div>
            <div class="flex flex-col gap-2 max-w-sm">
              <input v-model="pwCurrent" type="password" class="st-input w-full" placeholder="Current password" autocomplete="current-password" />
              <input v-model="pwNew" type="password" class="st-input w-full" placeholder="New password (8+ characters)" autocomplete="new-password" />
              <input
                v-model="pwConfirm" type="password" class="st-input w-full" placeholder="Confirm new password"
                autocomplete="new-password" @keydown.enter="onSavePassword"
              />
            </div>
            <div class="flex gap-2 mt-3">
              <button class="btn-deco btn-deco--gold btn-deco--sm" :disabled="pwSaving || !pwCurrent || !pwNew" @click="onSavePassword">
                {{ pwSaving ? 'Saving…' : 'Save password' }}
              </button>
              <button class="btn-deco btn-deco--ghost btn-deco--sm" @click="pwCurrent = ''; pwNew = ''; pwConfirm = ''">Clear</button>
            </div>
          </div>

          <div class="st-section">
            <div class="st-title">Contact support</div>
            <p class="font-body text-sm text-text-dim mb-3">
              Send word to the house. Replies come to <strong>{{ account.email }}</strong>.
            </p>
            <div class="flex flex-col gap-3 max-w-md">
              <input v-model="supportSubject" class="st-input w-full" placeholder="Subject" maxlength="150" />
              <textarea
                v-model="supportMessage" class="st-input w-full" rows="5" maxlength="4000"
                placeholder="What's the trouble? Give us the details."
              ></textarea>
              <button
                class="btn-deco btn-deco--gold btn-deco--sm self-start"
                :disabled="supportSending || !supportValid"
                @click="onContactSupport"
              >{{ supportSending ? 'Sending…' : 'Send message' }}</button>
            </div>
          </div>

          <!-- Deliberately last, and behind two gates: revealing the form, then
               the account password. Deleting ends every session for good. -->
          <div class="st-section">
            <div class="st-title">Close account</div>
            <template v-if="!deleteOpen">
              <p class="font-body text-sm text-text-dim mb-3">
                Closing your account ends it for good — your made man, your turf and your
                standing all go with it. Invoices already issued are kept, as the law requires.
              </p>
              <button class="btn-deco btn-deco--ghost btn-deco--sm" @click="deleteOpen = true">
                Close my account
              </button>
            </template>
            <template v-else>
              <div class="st-plate st-plate--red">
                <span>This cannot be undone. Enter your password to confirm.</span>
              </div>
              <div class="flex flex-col gap-3 max-w-sm mt-3">
                <input
                  v-model="deletePassword" type="password" class="st-input w-full"
                  placeholder="Your password" autocomplete="current-password"
                />
                <div class="flex gap-2">
                  <button
                    class="btn-deco btn-deco--gold btn-deco--sm"
                    :disabled="deleting || !deletePassword"
                    @click="onDeleteAccount"
                  >{{ deleting ? 'Closing…' : 'Delete for good' }}</button>
                  <button
                    class="btn-deco btn-deco--ghost btn-deco--sm"
                    :disabled="deleting"
                    @click="deleteOpen = false; deletePassword = ''"
                  >Keep my account</button>
                </div>
              </div>
            </template>
          </div>
        </template>

        <!-- ══ PAPERS (KYC) ══ -->
        <template v-else-if="tab === 'papers' && account">
          <div class="st-section">
            <div class="st-title">Identity papers</div>

            <p v-if="!account.kycEnabled" class="font-body text-sm text-text-dim italic">
              The house isn't checking papers right now — verification is disabled.
            </p>

            <template v-else>
              <div v-if="account.kycStatus === 'APPROVED'" class="st-plate st-plate--green">
                Papers in order — your identity is verified.
              </div>

              <div v-else-if="account.kycStatus === 'PENDING'" class="st-plate st-plate--amber">
                <span>
                  Your papers are with the Commission — under review.
                  <template v-if="account.kycSubmittedAt"> Submitted {{ dateOf(account.kycSubmittedAt) }}.</template>
                </span>
              </div>

              <template v-else>
                <div v-if="account.kycStatus === 'REJECTED'" class="st-plate st-plate--red">
                  <span>Papers rejected{{ account.kycRejectReason ? ` — ${account.kycRejectReason}` : '' }}. Submit again below.</span>
                </div>
                <p class="font-body text-sm text-text-dim mb-4">
                  Purchases over the house limit need verified papers. One government ID
                  (photo or PDF) and your particulars — reviewed by the Commission, usually within a day.
                </p>
                <div class="flex flex-col gap-3 max-w-md">
                  <div><div class="st-label mb-1">Full legal name</div>
                    <input v-model="kycName" class="st-input w-full" placeholder="As printed on the document" /></div>
                  <div><div class="st-label mb-1">Date of birth</div>
                    <input v-model="kycDob" type="date" class="st-input w-full" /></div>
                  <div><div class="st-label mb-1">Residential address</div>
                    <input v-model="kycAddress" class="st-input w-full" placeholder="Street, city, postal code" /></div>
                  <div><div class="st-label mb-1">Country</div>
                    <select v-model="kycCountry" class="st-input w-full">
                      <option value="" disabled>Select country…</option>
                      <option v-for="[code, name] in COUNTRIES" :key="code" :value="code">{{ name }}</option>
                    </select></div>
                  <div><div class="st-label mb-1">Identity document</div>
                    <input type="file" accept="image/*,.pdf" class="st-input st-file w-full" @change="onKycFile" /></div>
                  <button
                    class="btn-deco btn-deco--gold self-start"
                    :disabled="kycSubmitting || !kycFormValid"
                    @click="onSubmitKyc"
                  >{{ kycSubmitting ? 'Submitting…' : 'Submit papers' }}</button>
                </div>
              </template>
            </template>
          </div>

          <!-- House papers — the admin-published legal documents. Same source
               as the web footer; opens the standalone /legal/:slug page in a
               new tab so the game session behind it is left alone. -->
          <div class="st-section">
            <div class="st-title">House papers</div>
            <p v-if="legalPagesLoaded && !legalPages.length" class="font-body text-sm text-text-dim italic">
              No documents published.
            </p>
            <div v-else class="st-table">
              <a
                v-for="doc in legalPages"
                :key="doc.slug"
                class="st-tr text-gold hover:text-gold-bright"
                :href="`/legal/${encodeURIComponent(doc.slug)}`"
                target="_blank"
                rel="noopener"
              >
                <span class="text-text truncate">{{ doc.title }}</span>
                <span class="text-right text-text-dim">{{ dateOf(doc.updatedAt) }}</span>
              </a>
            </div>
          </div>
        </template>

        <!-- ══ REFERRAL ══ -->
        <template v-else-if="tab === 'referral'">
          <div class="st-section">
            <div class="st-title">Referral</div>

            <p v-if="referralError" class="font-body text-sm text-error">{{ referralError }}</p>
            <p v-else-if="!referralLoaded" class="font-body text-sm text-text-dim italic">Loading&hellip;</p>

            <template v-else-if="referral">
              <p class="font-body text-sm text-text-dim mb-4">
                Bring a friend into the family — they start with +{{ referral.reward }} cash.
              </p>
              <div class="st-row">
                <div><div class="st-label">Your link</div><div class="st-desc">Share it — new blood gets a head start.</div></div>
                <div class="flex items-center gap-2">
                  <input
                    class="st-input" :value="referralLink" readonly style="min-width: 240px"
                    @focus="($event.target as HTMLInputElement).select()"
                  />
                  <button class="btn-deco btn-deco--gold btn-deco--sm" @click="copyReferralLink">
                    {{ referralCopied ? 'Copied!' : 'Copy' }}
                  </button>
                </div>
              </div>
              <div class="st-row">
                <div><div class="st-label">Recruited</div><div class="st-desc">Friends who joined through your link.</div></div>
                <span class="st-badge st-badge--gold">{{ referral.referredCount }}</span>
              </div>
            </template>
          </div>
        </template>

        <!-- ══ PURCHASES ══ -->
        <template v-else-if="tab === 'purchases'">
          <div class="st-section">
            <div class="st-title">Purchases</div>
            <p v-if="purchasesLoaded && !purchases.length" class="font-body text-sm text-text-dim italic">No purchases yet.</p>
            <div v-else class="st-table">
              <div v-for="h in purchases" :key="h.id" class="st-tr">
                <span class="text-text-dim">{{ dateOf(h.createdAt) }}</span>
                <span class="text-text truncate">{{ h.pack }} — {{ h.bars.toLocaleString() }} bars</span>
                <span class="text-right text-text">{{ price(h.amountCents) }}</span>
                <span class="text-right" :class="h.status === 'COMPLETED' ? 'text-success' : 'text-text-dim'">{{ h.status }}</span>
              </div>
            </div>
          </div>

          <button class="btn-deco btn-deco--gold btn-deco--sm" @click="emit('goto-reserve')">
            Open The Gold Reserve
          </button>
        </template>

        <!-- ══ INVOICES ══ -->
        <template v-else-if="tab === 'invoices'">
          <div class="st-section">
            <div class="st-title">Invoices</div>
            <p v-if="invoicesLoaded && !invoices.length" class="font-body text-sm text-text-dim italic">No invoices yet.</p>
            <div v-else class="st-table">
              <div v-for="inv in invoices" :key="inv.invoiceNumber" class="st-tr">
                <span class="text-text-dim">{{ dateOf(inv.date) }}</span>
                <span class="text-text truncate">{{ inv.invoiceNumber }}</span>
                <span class="text-right text-text">{{ price(inv.totalCents) }}</span>
                <button
                  class="text-right text-gold hover:text-gold-bright disabled:opacity-50"
                  :disabled="downloading === inv.invoiceNumber"
                  @click="getPdf(inv.invoiceNumber)"
                >{{ downloading === inv.invoiceNumber ? '…' : 'PDF' }}</button>
              </div>
            </div>
          </div>
        </template>

        <!-- ══ TRANSACTIONS (full cash + gold ledger) ══ -->
        <template v-else-if="tab === 'transactions'">
          <div class="st-section">
            <div class="st-title">Transactions</div>
            <p v-if="ledgerLoaded && !ledger.length" class="font-body text-sm text-text-dim italic">
              No movements yet — every cash and gold-bar change shows up here.
            </p>
            <div v-else class="st-table">
              <div class="st-tr st-tr--tx st-tr--head">
                <span>When</span><span>Detail</span><span class="text-right">Amount</span><span class="text-right">Balance</span>
              </div>
              <div v-for="t in ledger" :key="t.id" class="st-tr st-tr--tx">
                <span class="text-text-dim">{{ timeOf(t.createdAt) }}</span>
                <span class="text-text truncate flex items-center gap-1.5">
                  <img :src="t.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ t.description }}
                </span>
                <span class="text-right font-heading tabular-nums" :class="t.amount >= 0 ? 'text-success' : 'text-error'">
                  {{ t.amount >= 0 ? '+' : '' }}{{ t.amount.toLocaleString() }}
                </span>
                <span class="text-right text-text-dim tabular-nums">{{ t.balanceAfter.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </template>

      </div>
    </div>
  </section>
</template>

<style scoped>
.st-body { display: flex; gap: 20px; align-items: flex-start; }

/* ── sidebar ── */
.st-nav {
  flex: none; width: 180px;
  display: flex; flex-direction: column; gap: 2px;
  border: 1px solid var(--color-border);
  background: color-mix(in srgb, var(--color-surface) 82%, transparent);
  padding: 10px 8px;
}
.st-nav-sec {
  font-family: var(--font-label, sans-serif);
  font-size: 8px; letter-spacing: 0.3em; text-transform: uppercase;
  color: var(--color-text-dim);
  padding: 4px 10px;
}
.st-item {
  text-align: left;
  padding: 8px 10px;
  font-family: var(--font-heading, serif);
  font-size: 13px; letter-spacing: 0.08em;
  color: var(--color-text-dim);
  border-left: 2px solid transparent;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.st-item:hover { color: var(--color-text); }
.st-item-active {
  color: var(--color-gold);
  border-left-color: var(--color-gold);
  background: rgba(200, 162, 76, 0.06);
}
.st-item-admin { color: #a78bfa; display: block; }
.st-item-admin:hover { color: #c4b5fd; }

/* ── sections ── */
.st-section {
  border: 1px solid var(--color-border);
  background: color-mix(in srgb, var(--color-surface) 82%, transparent);
  padding: 18px 20px;
  margin-bottom: 14px;
}
.st-title {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--font-heading, serif);
  font-size: 14px; letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--color-gold);
  margin-bottom: 14px;
}
.st-title::after {
  content: ''; flex: 1; height: 1px;
  background: linear-gradient(90deg, rgba(200, 162, 76, 0.3), transparent);
}

/* ── status plates ── */
.st-plate {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 13px; margin-bottom: 12px;
  font-family: var(--font-body, serif); font-size: 14px;
  border: 1px solid var(--color-border); color: var(--color-text);
}
.st-plate--green { border-color: rgba(34, 197, 94, 0.25); background: rgba(34, 197, 94, 0.06); color: #4ade80; }
.st-plate--amber { border-color: rgba(214, 158, 46, 0.35); background: rgba(214, 158, 46, 0.07); color: #d69e2e; justify-content: space-between; }
.st-plate--red { border-color: rgba(239, 68, 68, 0.3); background: rgba(239, 68, 68, 0.06); color: #f87171; }
.st-plate--dim { color: var(--color-text-dim); }

/* ── account card ── */
.st-card {
  display: flex; align-items: center; gap: 14px;
  padding: 14px;
  border: 1px solid rgba(200, 162, 76, 0.25);
  background: rgba(200, 162, 76, 0.04);
}
.st-avatar {
  flex: none; width: 64px; height: 64px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(200, 162, 76, 0.4);
  border-radius: 50%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
}
.st-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.st-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px;
  border: 1px solid var(--color-border);
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.st-badge--gold { border-color: rgba(200, 162, 76, 0.4); color: var(--color-gold); }

/* ── rows + inputs ── */
.st-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent);
}
.st-row:last-child { border-bottom: none; }
.st-label {
  font-family: var(--font-heading, serif);
  font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--color-text);
}
.st-desc { font-family: var(--font-body, serif); font-size: 12px; color: var(--color-text-dim); font-style: italic; }
.st-input {
  padding: 7px 10px;
  border: 1px solid var(--color-border);
  background: rgba(0, 0, 0, 0.3);
  font-family: var(--font-body, serif); font-size: 14px;
  color: var(--color-text);
  min-width: 160px;
}
.st-input:focus { outline: none; border-color: rgba(200, 162, 76, 0.5); }
.st-input[readonly] { color: var(--color-text-dim); }
.st-file { padding: 5px 8px; font-size: 13px; }
select.st-input option { background: #17131f; }

/* ── purchase/invoice tables ── */
.st-table { display: flex; flex-direction: column; }
.st-tr {
  display: grid; grid-template-columns: 110px 1fr 90px 100px;
  gap: 10px; align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent);
  font-family: var(--font-body, serif); font-size: 13px;
}
.st-tr:last-child { border-bottom: none; }
.st-tr--tx { grid-template-columns: 130px 1fr 100px 100px; }
.st-tr--head {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; font-weight: 600;
  letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--color-text-dim);
}

@media (max-width: 760px) {
  .st-body { flex-direction: column; }
  .st-nav { width: 100%; flex-direction: row; flex-wrap: wrap; align-items: center; }
  /* each section label breaks to its own line so groups stay legible */
  .st-nav-sec { width: 100%; }
  .st-item { border-left: 0; border-bottom: 2px solid transparent; }
  .st-item-active { border-left-color: transparent; border-bottom-color: var(--color-gold); }
}

/* Billing tables: fixed-column grids can't fit a phone width, so each row
   stacks — the name/detail on its own line, the rest inline under it. Keeps
   the page to vertical scroll only. */
@media (max-width: 560px) {
  .st-tr, .st-tr--tx {
    display: flex; flex-wrap: wrap; align-items: baseline;
    grid-template-columns: none; gap: 2px 12px; padding: 10px 4px;
  }
  .st-tr > * { text-align: left !important; font-size: 12px; }
  .st-tr > :nth-child(2) { order: -1; flex-basis: 100%; font-size: 14px; }
  /* column-header row is meaningless once rows stack — hide it (needs to beat
     the .st-tr flex rule above, so match with two classes) */
  .st-tr.st-tr--head { display: none; }
}
</style>
